const GLOBAL_STORAGE_KEY = 'non-farsi-content-hint-suppressed';
const LEGACY_STORAGE_PREFIX = 'non-farsi-content-hint-suppressed:';
const PENDING_HINT_KEY = 'non-farsi-content-hint-pending';
const SKIP_ONCE_KEY = 'non-farsi-content-hint-skip-once';

export type NonFarsiContentKind = 'lesson' | 'quiz' | 'exercise';

export function nonFarsiContentKey(kind: NonFarsiContentKind, id: number): string {
  return `${kind}:${id}`;
}

function clearLegacyNonFarsiContentHintSuppressionKeys(): void {
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(LEGACY_STORAGE_PREFIX)) {
        toRemove.push(key);
      }
    }
    toRemove.forEach((key) => localStorage.removeItem(key));
  } catch {
    /* ignore quota / private mode */
  }
}

/** Global "do not show again" — applies to all English-locked lessons, quizzes, and exercises. */
export function isNonFarsiContentHintSuppressed(): boolean {
  try {
    if (localStorage.getItem(GLOBAL_STORAGE_KEY) === '1') {
      return true;
    }
    // Migrate legacy per-content keys to the global preference.
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(LEGACY_STORAGE_PREFIX) && localStorage.getItem(key) === '1') {
        localStorage.setItem(GLOBAL_STORAGE_KEY, '1');
        clearLegacyNonFarsiContentHintSuppressionKeys();
        return true;
      }
    }
    return false;
  } catch {
    return false;
  }
}

export function setNonFarsiContentHintSuppressed(): void {
  try {
    localStorage.setItem(GLOBAL_STORAGE_KEY, '1');
    clearLegacyNonFarsiContentHintSuppressionKeys();
  } catch {
    /* ignore quota / private mode */
  }
}

export function clearNonFarsiContentHintSuppressed(): void {
  try {
    localStorage.removeItem(GLOBAL_STORAGE_KEY);
    clearLegacyNonFarsiContentHintSuppressionKeys();
  } catch {
    /* ignore quota / private mode */
  }
}

/** Stored before navigation so the destination page can show the hint after content opens. */
export function setPendingNonFarsiContentHint(contentKey: string): void {
  try {
    sessionStorage.setItem(PENDING_HINT_KEY, contentKey);
  } catch {
    /* ignore quota / private mode */
  }
}

export function consumePendingNonFarsiContentHint(expectedKey: string): boolean {
  try {
    const pending = sessionStorage.getItem(PENDING_HINT_KEY);
    if (pending !== expectedKey) return false;
    sessionStorage.removeItem(PENDING_HINT_KEY);
    return true;
  } catch {
    return false;
  }
}

/** Skip the English-primary hint once (e.g. dashboard welcome lesson card). */
export function setSkipNonFarsiContentHintOnce(contentKey: string): void {
  try {
    sessionStorage.setItem(SKIP_ONCE_KEY, contentKey);
    const pending = sessionStorage.getItem(PENDING_HINT_KEY);
    if (pending === contentKey) {
      sessionStorage.removeItem(PENDING_HINT_KEY);
    }
  } catch {
    /* ignore quota / private mode */
  }
}

export function consumeSkipNonFarsiContentHintOnce(expectedKey: string): boolean {
  try {
    const skip = sessionStorage.getItem(SKIP_ONCE_KEY);
    if (skip !== expectedKey) return false;
    sessionStorage.removeItem(SKIP_ONCE_KEY);
    return true;
  } catch {
    return false;
  }
}

export function isDesignedForNonFarsiUsers(
  item: { isDesignedForNonFarsiUsers?: boolean; IsDesignedForNonFarsiUsers?: boolean } | null | undefined
): boolean {
  if (!item) return false;
  return Boolean(item.isDesignedForNonFarsiUsers ?? item.IsDesignedForNonFarsiUsers);
}
